import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';
import SchoolIcon from '@material-ui/icons/SchoolOutlined';
import LockIcon from '@material-ui/icons/LockOutlined';
import Check from '@material-ui/icons/Check';
import Verified from '@material-ui/icons/CheckCircleOutlineOutlined';
import WarningIcon from '@material-ui/icons/ErrorOutlineOutlined';
import AssignmentIcon from '@material-ui/icons/AssignmentOutlined';
import { Header, Page, Content } from '@backstage/core-components';
import { useNavigate } from 'react-router-dom';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { Role } from './types';
import { StepModal } from './StepModal';
import { NpsModal } from './NpsModal';
import { useOnboardingContext, OnboardingSession } from './OnboardingContext';
import { getJourneySteps } from './DevSteps/journeySteps';
import { useOnboardingTranslation } from '../../translations';
import { LanguageSelector } from './LanguageSelector';
import { getOnboardingColors } from './colors';
import { OnboardingApiClient } from '../../api/onboardingApi';

const useStyles = makeStyles(theme => {
  const c = getOnboardingColors(theme);
  return {
  journeyCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: 'none',
  },
  activeCard: {
    backgroundColor: theme.palette.background.paper,
  },
  completedCard: {
    backgroundColor: theme.palette.background.paper,
  },
  inactiveCard: {
    backgroundColor: theme.palette.background.paper,
  },
  cardContentActive: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(4, 3),
    flexGrow: 1,
    '&:last-child': {
      paddingBottom: theme.spacing(4),
    },
  },
  cardContentInactive: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(4, 3),
    flexGrow: 1,
    '&:last-child': {
      paddingBottom: theme.spacing(4),
    },
  },
  iconActive: {
    fontSize: 48,
    color: c.iconDefault,
    marginBottom: theme.spacing(1.5),
  },
  iconLocked: {
    fontSize: 48,
    color: c.iconDefault,
    marginBottom: theme.spacing(1.5),
  },
  iconCompleted: {
    fontSize: 48,
    color: c.iconDefault,
    marginBottom: theme.spacing(1.5),
  },
  journeyLabelActive: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  journeyLabel: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  journeyTitleActive: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: c.primary,
    marginBottom: theme.spacing(1.5),
  },
  journeyTitleInactive: {
    fontWeight: 700,
    fontSize: '1.15rem',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  journeyTitleCompleted: {
    fontWeight: 700,
    fontSize: '1.15rem',
    marginBottom: theme.spacing(2),
    color: c.success,
  },
  description: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
  badgeRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  badgeActive: {
    fontSize: '0.688rem',
    lineHeight: '1rem',
    padding: '4px 8px',
    borderRadius: 16,
    backgroundColor: c.badgeBg,
    border: `1px solid ${c.badgeBorder}`,
    color: c.badgeText,
    fontWeight: 400,
    whiteSpace: 'nowrap' as const,
  },
  badgeCompleted: {
    fontSize: '0.688rem',
    lineHeight: '1rem',
    padding: '4px 8px',
    borderRadius: 16,
    backgroundColor: c.badgeBg,
    border: `1px solid ${c.success}`,
    color: c.success,
    whiteSpace: 'nowrap' as const,
  },
  phaseListActive: {
    textAlign: 'left',
    width: '100%',
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(3),
    flexGrow: 1,
    '& li': {
      marginBottom: theme.spacing(1),
      fontSize: '0.9rem',
      color: theme.palette.text.primary,
    },
  },
  phaseListInactive: {
    textAlign: 'left',
    width: '100%',
    paddingLeft: theme.spacing(2),
    flexGrow: 1,
    marginTop: theme.spacing(1),
  },
  phaseItem: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  startButton: {
    textTransform: 'none',
    marginTop: 'auto',
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    padding: theme.spacing(1, 1.5),
    borderRadius: 4,
    backgroundColor: c.buttonPrimaryBg,
    color: c.buttonPrimaryFg,
    '&:hover': {
      backgroundColor: c.primaryHover,
    },
  },
  reviewButton: {
    textTransform: 'none',
    marginTop: 'auto',
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    padding: theme.spacing(1, 1.5),
    borderRadius: 4,
    borderColor: c.primary,
    color: c.primary,
    '&:hover': {
      borderColor: c.primaryHover,
      color: c.primaryHover,
    },
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    backgroundColor: c.warningBannerBg,
    borderRadius: 8,
    padding: theme.spacing(1, 2),
    marginTop: theme.spacing(6),
    maxWidth: 'fit-content',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxShadow: `0 2px 4px ${c.warningBannerFg}33`,
  },
  warningIcon: {
    color: c.warningBannerFg,
    fontSize: 20,
  },
  warningText: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: c.warningBannerFg,
  },
  successColor: {
    color: c.success,
  },
  };
});

interface TrackPageProps {
  role: Role;
  completedJourneys: string[];
  onCompleteJourney: (journeyId: string) => void;
  apiClient?: OnboardingApiClient;
  npsCompleted?: boolean;
  onNpsCompleted?: () => void;
}

export const TrackPage = ({
  role,
  completedJourneys,
  onCompleteJourney,
  apiClient,
  npsCompleted = false,
  onNpsCompleted,
}: TrackPageProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const alertApi = useApi(alertApiRef);
  const theme = useTheme();
  const oColors = getOnboardingColors(theme);
  const { startSession } = useOnboardingContext();
  const { t, language } = useOnboardingTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeJourneyIndex, setActiveJourneyIndex] = useState<number | null>(
    null,
  );
  const [currentStep, setCurrentStep] = useState(1);
  const [npsOpen, setNpsOpen] = useState(false);
  const totalSteps = 3;

  const handleOpenModal = (journeyIndex: number) => {
    handleOpenJourney(journeyIndex, false);
  };

  const handleReviewJourney = (journeyIndex: number) => {
    handleOpenJourney(journeyIndex, true);
  };

  const handleOpenJourney = (journeyIndex: number, isReview: boolean) => {
    const steps = getJourneySteps(
      journeyIndex,
      role.journeys[journeyIndex].title,
      t,
      language,
      oColors,
    );

    if (steps.length > 0) {
      const journeyId = role.journeys[journeyIndex].id;
      const session: OnboardingSession = {
        roleId: role.id,
        journeyIndex,
        steps,
        currentStepIndex: 0,
        returnPath: `/onboarding/${role.id}`,
        onComplete: isReview
          ? undefined
          : () => {
              try {
                const saved = localStorage.getItem('onboarding_completed_journeys');
                const completedList: string[] = saved ? JSON.parse(saved) : [];
                if (!completedList.includes(journeyId)) {
                  completedList.push(journeyId);
                  localStorage.setItem(
                    'onboarding_completed_journeys',
                    JSON.stringify(completedList),
                  );
                }
              } catch {
                // ignore
              }

              // Also persist to the database
              if (apiClient) {
                apiClient.completeJourney(journeyId, role.id).catch(() => {
                  // DB save failed silently
                });
              }
            },
      };
      startSession(session);
      navigate('/');
      return;
    }

    setActiveJourneyIndex(journeyIndex);
    setCurrentStep(1);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setActiveJourneyIndex(null);
    setCurrentStep(1);
  };

  const handleContinue = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      if (activeJourneyIndex !== null) {
        onCompleteJourney(role.journeys[activeJourneyIndex].id);
      }
      handleCloseModal();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const isJourneyUnlocked = (index: number): boolean => {
    if (index === 0) return true;
    return completedJourneys.includes(role.journeys[index - 1].id);
  };

  const getFirstActiveIndex = (): number => {
    for (let i = 0; i < role.journeys.length; i++) {
      const unlocked = isJourneyUnlocked(i);
      const completed = completedJourneys.includes(role.journeys[i].id);
      if (unlocked && !completed) return i;
    }
    return -1;
  };

  const firstActiveIndex = getFirstActiveIndex();

  return (
    <Page themeId="tool">
      <Header
        title={`${role.title} Onboarding`}
        subtitle={t('trackPageSubtitle')}
      />
      <Content>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 8,
            gap: 8,
          }}
        >
          <LanguageSelector />
        </Box>
        <Grid
          container
          spacing={4}
          style={{ justifyContent: 'center', marginTop: 16 }}
        >
          {role.journeys.map((journey, index) => {
            const completed = completedJourneys.includes(journey.id);
            const isActive = index === firstActiveIndex;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card
                  className={`${classes.journeyCard} ${
                    isActive
                      ? classes.activeCard
                      : completed
                      ? classes.completedCard
                      : classes.inactiveCard
                  }`}
                >
                  <CardContent
                    className={
                      isActive
                        ? classes.cardContentActive
                        : classes.cardContentInactive
                    }
                  >
                    {/* Icon */}
                    {completed ? (
                      <Verified className={classes.iconCompleted} />
                    ) : isActive ? (
                      <SchoolIcon className={classes.iconActive} />
                    ) : (
                      <LockIcon className={classes.iconLocked} />
                    )}

                    {/* Journey label */}
                    <Typography
                      className={
                        isActive
                          ? classes.journeyLabelActive
                          : classes.journeyLabel
                      }
                    >
                      {t('journeyLabel', { index: index + 1 })}
                    </Typography>

                    {/* Title */}
                    <Typography
                      className={
                        isActive
                          ? classes.journeyTitleActive
                          : completed
                          ? classes.journeyTitleCompleted
                          : classes.journeyTitleInactive
                      }
                    >
                      {journey.title}
                    </Typography>

                    {/* Active card: description, badges, numbered list, button */}
                    {/* {isActive && ( */}
                    <>
                      <Typography className={classes.description}>
                        {journey.description}
                      </Typography>

                      <Box className={classes.badgeRow}>
                        {completed ? (
                          <Typography
                            component="span"
                            className={classes.badgeCompleted}
                          >
                            {t('completed')}
                          </Typography>
                        ) : (
                          <>
                            <Typography
                              component="span"
                              className={classes.badgeActive}
                            >
                              {t('stepsCount', { count: journey.totalSteps })}
                            </Typography>
                            <Typography
                              component="span"
                              className={classes.badgeActive}
                            >
                              {t('phasesCount', { count: journey.phases.length })}
                            </Typography>
                            <Typography
                              component="span"
                              className={classes.badgeActive}
                            >
                              {journey.duration}
                            </Typography>
                          </>
                        )}
                      </Box>

                      {completed ? (
                        <Box
                          className={classes.phaseListActive}
                          component="div"
                          style={{ listStyle: 'none', paddingLeft: 0 }}
                        >
                          {journey.phases.map((phase, i) => (
                            <Typography
                              key={i}
                              style={{
                                marginBottom: 8,
                                fontSize: '0.9rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <Check
                                style={{ fontSize: 18 }}
                                className={classes.successColor}
                              />
                              {phase}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <ol className={classes.phaseListActive}>
                          {journey.phases.map((phase, i) => (
                            <li key={i}>{phase}</li>
                          ))}
                        </ol>
                      )}

                      {isActive && (
                        <Button
                          variant="contained"
                          className={classes.startButton}
                          onClick={() => handleOpenModal(index)}
                        >
                          {t('startJourney')}
                        </Button>
                      )}

                      {completed && (
                        <Button
                          variant="outlined"
                          className={classes.reviewButton}
                          onClick={() => handleReviewJourney(index)}
                        >
                          {t('reviewJourney')}
                        </Button>
                      )}
                    </>
                    {/* )} */}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {/* NPS Card — follows the same unlock strategy as journeys */}
          {role.id === 'developer' && (() => {
            const npsUnlocked = completedJourneys.length >= role.journeys.length;
            const isNpsActive = npsUnlocked && firstActiveIndex === -1 && !npsCompleted;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card
                  className={`${classes.journeyCard} ${
                    npsCompleted
                      ? classes.completedCard
                      : isNpsActive
                      ? classes.activeCard
                      : classes.inactiveCard
                  }`}
                >
                  <CardContent
                    className={
                      isNpsActive || npsCompleted
                        ? classes.cardContentActive
                        : classes.cardContentInactive
                    }
                  >
                    {npsCompleted ? (
                      <AssignmentIcon className={classes.iconCompleted} />
                    ) : isNpsActive ? (
                      <AssignmentIcon className={classes.iconActive} />
                    ) : (
                      <LockIcon className={classes.iconLocked} />
                    )}

                    <Typography
                      className={
                        isNpsActive
                          ? classes.journeyLabelActive
                          : classes.journeyLabel
                      }
                    >
                      NPS
                    </Typography>

                    <Typography
                      className={
                        npsCompleted
                          ? classes.journeyTitleCompleted
                          : isNpsActive
                          ? classes.journeyTitleActive
                          : classes.journeyTitleInactive
                      }
                    >
                      {t('npsTitle')}
                    </Typography>

                    <Typography className={classes.description}>
                      {t('npsDescription')}
                    </Typography>

                    <Box className={classes.badgeRow}>
                      {npsCompleted ? (
                        <Typography
                          component="span"
                          className={classes.badgeCompleted}
                        >
                          {t('completed')}
                        </Typography>
                      ) : (
                        <Typography
                          component="span"
                          className={classes.badgeActive}
                        >
                          {t('npsQuestionsCount', { count: 11 })}
                        </Typography>
                      )}
                    </Box>

                    {isNpsActive && (
                      <Button
                        variant="contained"
                        className={classes.startButton}
                        onClick={() => setNpsOpen(true)}
                      >
                        {t('npsStart')}
                      </Button>
                    )}

                    {npsCompleted && (
                      <Button
                        variant="outlined"
                        className={classes.reviewButton}
                        onClick={() => setNpsOpen(true)}
                      >
                        {t('reviewNps')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })()}
        </Grid>

        {/* Warning Banner */}
        {completedJourneys.length < role.journeys.length && (
          <Box className={classes.warningBanner}>
            <WarningIcon className={classes.warningIcon} />
            <Typography className={classes.warningText}>
              {t('warningMessage')}
            </Typography>
          </Box>
        )}

        {/* NPS Modal */}
        <NpsModal
          open={npsOpen}
          onClose={() => {
            setNpsOpen(false);
            if (apiClient) {
              apiClient.getProgress().then(progress => {
                if (progress.nps_completed) {
                  onNpsCompleted?.();
                }
              }).catch(() => {});
            }
          }}
          onSubmit={async (answers) => {
            if (apiClient) {
              try {
                await apiClient.submitNps(answers);
                onNpsCompleted?.();
                return true;
              } catch {
                alertApi.post({
                  message: t('npsSubmitError'),
                  severity: 'error',
                });
                return false;
              }
            }
            return true;
          }}
          language={language}
          subtitle="NPS"
          title={t('npsTitle')}
        />

        {/* Step Modal */}
        {activeJourneyIndex !== null && (
          <StepModal
            open={modalOpen}
            onClose={handleCloseModal}
            journeyTitle={`${t('journeyLabel', { index: activeJourneyIndex + 1 })}: ${
              role.journeys[activeJourneyIndex].title
            }`}
            sectionTitle={`${activeJourneyIndex + 1}. ${
              role.journeys[activeJourneyIndex].title
            }`}
            currentStep={currentStep}
            totalSteps={totalSteps}
            stepTitle={`${t('stepIndicator', { current: currentStep, total: totalSteps })}`}
            onBack={currentStep > 1 ? handleBack : undefined}
            onContinue={handleContinue}
            continueLabel={currentStep === totalSteps ? t('finish') : t('continueLabel')}
            backLabel={t('back')}
          >
            <Typography>
              Content for step {currentStep} of journey "
              {role.journeys[activeJourneyIndex].title}"
            </Typography>
          </StepModal>
        )}
      </Content>
    </Page>
  );
};
